import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import ContactHero from './components/ContactHero';
import ContactForm from './components/ContactForm';
import CompanyInfo from './components/CompanyInfo';
import MapSection from './components/MapSection';
import SupportHours from './components/SupportHours';
import NewsletterSection from './components/NewsletterSection';
import AccountCTA from './components/AccountCTA';
import SMSOptinSection from './components/SMSOptinSection';

const NordPassContactPage = () => {
  return (
    <>
      <Helmet>
        <title>Kontakt oss — NordPass</title>
        <meta name="description" content="Ta kontakt med NordPass-teamet. Vi hjelper deg med abonnement, teknisk støtte og alt om norske veier." />
        <link
          href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Lora:ital,wght@0,400;0,700;1,400;1,700&family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500;700&display=swap"
          rel="stylesheet" />
        
      </Helmet>
      <div style={{ background: '#050A12', minHeight: '100vh', overflowX: 'hidden' }}>

        {/* Sticky nav — identical to landing page */}
        <nav style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
          background: 'rgba(5,10,18,0.92)',
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)'
        }}>
          <div style={{
            maxWidth: '1400px', margin: '0 auto', padding: '0 5%',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            height: '72px'
          }}>
            <Link to="/nord-pass-landing-page" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
              <div style={{
                width: '32px', height: '32px', background: '#3B6FD4',
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
              }} />
              <span style={{
                fontFamily: 'Syne, sans-serif', fontSize: '1.125rem',
                fontWeight: 800, color: '#EEF0F2', letterSpacing: '-0.03em'
              }}>NordPass</span>
            </Link>

            <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
              {[
              { label: 'Hjem', to: '/nord-pass-landing-page' },
              { label: 'Veikart', to: '/nordic-map-dashboard' },
              { label: 'Bomkalkulator', to: '/toll-calculator' },
              { label: 'Kontakt', to: '/nord-pass-contact-page' }]?.
              map((link) =>
              <Link
                key={link?.to}
                to={link?.to}
                style={{
                  fontFamily: 'Syne, sans-serif', fontSize: '0.8rem',
                  fontWeight: 600, color: link?.to === '/nord-pass-contact-page' ? '#E8941A' : 'rgba(238,240,242,0.55)',
                  textDecoration: 'none', letterSpacing: '0.05em',
                  textTransform: 'uppercase', transition: 'color 0.2s'
                }}>
                {link?.label}</Link>
              )}
              <Link
                to="/login"
                style={{
                  fontFamily: 'Syne, sans-serif', fontSize: '0.8rem',
                  fontWeight: 700, color: '#EEF0F2', textDecoration: 'none',
                  letterSpacing: '0.05em', textTransform: 'uppercase',
                  background: '#3B6FD4', padding: '8px 20px',
                  clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))'
                }}>
                Logg inn</Link>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <ContactHero />

        {/* Main content — asymmetric grid */}
        <section style={{
          background: '#050A12',
          padding: '100px 5%',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Diagonal cut background */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(135deg, rgba(59,111,212,0.04) 0%, transparent 50%, rgba(232,148,26,0.03) 100%)',
            pointerEvents: 'none'
          }} />

          <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            {/* Asymmetric grid: form (wider) + company info */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1.2fr 1fr',
              gap: '80px',
              alignItems: 'start'
            }}>
              {/* Contact form */}
              <div style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
                padding: '48px',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Norwegian landscape texture strip */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: '120px',
                  overflow: 'hidden', opacity: 0.06
                }}>
                  <img
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_11a56f8e3-1772188994805.png"
                    alt="Norwegian mountain landscape texture"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(1)' }} />
                  
                </div>
                <ContactForm />
              </div>

              {/* Company info */}
              <div style={{ paddingTop: '20px' }}>
                <CompanyInfo />

                {/* Polaroid photo */}
                <div style={{
                  marginTop: '48px',
                  background: '#0D1520',
                  padding: '12px 12px 40px',
                  transform: 'rotate(-2deg)',
                  display: 'inline-block',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  maxWidth: '280px'
                }}>
                  <img
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_16f11e4f5-1772188997722.png"
                    alt="NordPass Oslo office building entrance on a winter morning with snow-covered street"
                    style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block', filter: 'brightness(0.85) saturate(0.7)' }} />
                  
                  <div style={{
                    fontFamily: 'Georgia, serif', fontStyle: 'italic',
                    fontSize: '0.7rem', color: 'rgba(238,240,242,0.4)',
                    textAlign: 'center', marginTop: '12px',
                    letterSpacing: '0.05em'
                  }}>Storgata 47, Oslo — 2026</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <MapSection />

        {/* Support hours + social */}
        <SupportHours />

        {/* Newsletter opt-in */}
        <NewsletterSection />
        <SMSOptinSection />

        {/* Account registration CTA */}
        <AccountCTA />

        {/* Footer strip */}
        <footer style={{
          background: '#050A12',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          padding: '40px 5%'
        }}>
          <div style={{
            maxWidth: '1400px', margin: '0 auto',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '16px'
          }}>
            <div style={{
              fontFamily: 'Syne, sans-serif', fontSize: '1rem',
              fontWeight: 800, color: '#EEF0F2', letterSpacing: '-0.03em'
            }}>NordPass</div>
            <div style={{
              fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: '0.8rem', color: 'rgba(238,240,242,0.3)'
            }}>© 2026 NordPass AS — Norges veier, din kontroll.</div>
            <div style={{ display: 'flex', gap: '24px' }}>
              {[
              { label: 'Hjem', to: '/nord-pass-landing-page' },
              { label: 'Personvern', to: '#' },
              { label: 'Vilkår', to: '#' }]?.
              map((l) =>
              <Link key={l?.to} to={l?.to} style={{
                fontFamily: 'monospace', fontSize: '0.6rem',
                letterSpacing: '0.2em', color: 'rgba(238,240,242,0.3)',
                textDecoration: 'none', textTransform: 'uppercase'
              }}>{l?.label}</Link>
              )}
            </div>
          </div>
        </footer>
      </div>
    </>);

};

export default NordPassContactPage;